import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	signal,
	inject,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { mergeMap, Observable, Subject } from 'rxjs';
import { RightSidebarNavigationService } from '@layout/au-right-sidebar/right-sidebar-navigation.service';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { Store } from '@ngrx/store';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { AuUtilsFunctions } from '@app/shared';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import {
	attachedEntityTableConfig,
	sidebarHeaders,
} from '@app/pages/cmms/configs/cmms-table-configs';
import { AssetsService } from '@app/pages/cmms/services/assets.service';
import { editFileSuccess } from '@app/pages/cmms/components/files/state/files.action';
import {
	filterEntityByKey,
	getPrivateKeys,
} from '@app/pages/cmms/utils-and-classes/cmms-util-functions';
import {
	ICmmsAsset,
	ICmmsAssetCreateVariables,
	ICmmsAssetEntityToAttach,
} from '@app/pages/cmms/interfaces/assets.interfaces';
import { WorkOrdersService } from '@app/pages/cmms/services/work-orders.service';
import { IncidentsService } from '@app/pages/cmms/services/incidents.service';
import { RequestsService } from '@app/pages/cmms/services/requests.service';
import { EntityType } from '@layout/state/layout.reducer';
import { FilesService } from '@app/pages/cmms/services/files.service';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import {
	SetAttachFormValue,
	SetRightSidebarComponentContext,
} from '@layout/state/layout.actions';
import {
	clearAssetExternalData,
	startCreateCmmsAsset,
} from '@app/pages/cmms/components/assets/state/assets.actions';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { TableType } from '@app/pages/cmms/types';
import { iconEnum } from '@app/configs/model/IconEnum';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IDroppedFile } from '@shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { IClassification } from '@app/pages/cmms/interfaces/classification.interfaces';
import { IFileLoaderOptions } from '@shared/ui-components/au-file-loader/services/file-loader.service';
import { RightSidebarAliasesEnum } from '@layout/au-right-sidebar/au-right-sidebar-component-aliases';
import { FileSelectionsService } from '@shared/ui-components/au-file-loader/services/file-selections.service';
import { CmmsFiltersService } from '@app/pages/cmms/components/cmms-sort-filters-grouping/services/cmms-filters.service';
import { assetData } from '@app/pages/cmms/components/assets/state/assets.selectors';
import { CommonModule, DatePipe } from '@angular/common';
import { CmmsEntityNameComponent } from '../../shared-form-group-controls/cmms-entity-name/cmms-entity-name.component';
import { CmmsLocationsSectionComponent } from '../../shared-form-group-controls/cmms-locations-section/cmms-locations-section.component';
import { ImageSectionComponent } from '../../files/image-section/image-section.component';
import { CmmsDescriptionComponent } from '../../shared-form-group-controls/cmms-description/cmms-description.component';
import { CmmsLabelsSectionComponent } from '../../shared-form-group-controls/cmms-labels-section/cmms-labels-section.component';
import { MaintenanceSectionComponent } from '../../maintenance-section/maintenance-section.component';
import { AssetConditionGradeComponent } from '../asset-condition-grade/asset-condition-grade.component';
import { CmmsSimpleTextareaComponent } from '../../shared-form-group-controls/cmms-simple-textarea/cmms-simple-textarea.component';
import { AuMaintenanceSelectorComponent } from '../../au-maintenance-selector/au-maintenance-selector.component';
import { AuCmmsCostComponent } from '../../au-cmms-cost/au-cmms-cost.component';
import { CmmsSimpleInputComponent } from '../../shared-form-group-controls/cmms-simple-input/cmms-simple-input.component';
import { CmmsYearSelectorComponent } from '../../shared-form-group-controls/cmms-year-selector/cmms-year-selector.component';
import { CmmsDatePickerComponent } from '../../shared-form-group-controls/cmms-date-picker/cmms-date-picker.component';
import { CmmsCostInputComponent } from '../../shared-form-group-controls/cmms-cost-input/cmms-cost-input.component';
import { CmmsClassificationComponent } from '../../shared-form-group-controls/cmms-classification/cmms-classification.component';
import { EntitySectionComponent } from '../../entity-section/entity-section.component';
import { FileSectionComponent } from '../../files/file-section/file-section.component';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { timeframeMap } from '@app/pages/cmms/components/assets/models/timeframe-status';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { AiImageAnalysisLoadingComponent } from '@app/pages/cmms/components/ai-image-analysis-loading/ai-image-analysis-loading.component';
import { IAiAssetAnalysisResponse } from '@app/shared/services/image-analysis.service';
import { AiAssetImageAnalysisCardComponent } from '../../ai-asset-image-analysis-card/ai-asset-image-analysis-card.component';
import { AiSearchSuggestionsComponent } from '../../ai-search-suggestions/ai-search-suggestions.component';
import { IAttachedFile } from '@app/pages/cmms/interfaces/files.interfaces';

@Component({
	selector: 'new-asset',
	templateUrl: './new-asset.component.html',
	styleUrls: ['./new-asset.component.scss'],
	providers: [CmmsFiltersService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		CmmsEntityNameComponent,
		CmmsLocationsSectionComponent,
		ImageSectionComponent,
		CmmsDescriptionComponent,
		CmmsLabelsSectionComponent,
		MaintenanceSectionComponent,
		AssetConditionGradeComponent,
		CmmsSimpleTextareaComponent,
		AuMaintenanceSelectorComponent,
		AuCmmsCostComponent,
		CmmsSimpleInputComponent,
		CmmsYearSelectorComponent,
		CmmsDatePickerComponent,
		CmmsCostInputComponent,
		CmmsClassificationComponent,
		EntitySectionComponent,
		FileSectionComponent,
		NgxTolgeeModule,
		AiAssetImageAnalysisCardComponent,
		AiImageAnalysisLoadingComponent,
		AiSearchSuggestionsComponent,
	],
})
export class NewAssetComponent implements OnInit, OnDestroy, AfterViewInit {
	private readonly translateService = inject(TranslateService);
	private readonly featureFlagsService = inject(FeatureFlagService);

	assetForm: FormGroup = this.fb.group({
		requests: [],
		incidents: [],
		workOrders: [],
		files: [[]],
		classifications: [],
		coordinates: [],
	});
	isFullscreen$: Observable<boolean>;
	actionType: string;

	editEntityType: EntityType;
	currentEntityDataForEdit: any;
	fileLoaderOptions: Partial<IFileLoaderOptions> = {
		mutationVariables: {
			isCreationInProgress: true,
		},
	};
	timeframeSignal = signal('');

	private destroy$: Subject<void> = new Subject<void>();
	private initialFormValue;
	private isProcessing: boolean;

	readonly iconEnum = iconEnum;
	readonly cmmsPages = CmmsPages;
	readonly tableType = TableType;
	readonly tableConfig: ICMMSColumn[] = attachedEntityTableConfig;
	readonly conditionGradeExpanded = signal<boolean>(false);
	protected readonly featureFlagService = inject(FeatureFlagService);

	externalData: any;

	enableMatterportIntegration = this.featureFlagsService.isFFEnabled(
		'enable-matterport-integration'
	);
	isAiAnalysisLoading = signal<boolean>(false);
	aiAnalysisResponse = signal<IAiAssetAnalysisResponse | null>(null);

	constructor(
		private fb: FormBuilder,
		private sidebarNavigation: RightSidebarNavigationService,
		private sidebarService: AuRightSidebarService,
		private store$: Store,
		private assetsService: AssetsService,
		private cdr: ChangeDetectorRef,
		private workOrdersService: WorkOrdersService,
		private incidentService: IncidentsService,
		private requestService: RequestsService,
		private filesService: FilesService,
		private notify: IconSnackBarService,
		private fileSelectionsService: FileSelectionsService,
		private datePipe: DatePipe
	) {}

	ngOnInit(): void {
		this.isFullscreen$ = this.sidebarService.sidebarFullscreen$;
		this.setConfigurationForComponent();
		this.trackSidebarSubmitAction();
		this.trackSidebarCloseAction();
	}

	ngAfterViewInit() {
		this.getFormValueFromStore();

		this.store$
			.select(assetData)
			.pipe(
				takeUntil(this.destroy$),
				filter(data => !!data)
			)
			.subscribe(data => {
				this.externalData = data;
				this.assetForm.patchValue({
					// yes it locationS its correct
					// TODO refactor it to location
					location: this.externalData?.locations,
					coordinates: this.externalData?.coordinates,
				});
				this.initialFormValue = this.assetForm.value;
			});
	}

	getFormValueFromStore() {
		this.sidebarNavigation
			.getInitialFormValueByPage(CmmsPages.newAsset)
			.subscribe(sideBar => {
				this.assetForm.patchValue(sideBar.formValue);
				this.conditionGradeExpanded.set(
					sideBar.formValue.conditionGrade !== null ||
						sideBar.formValue.conditionGrade !== undefined
				);
			});
	}

	setConfigurationForComponent() {
		this.sidebarNavigation.pageContext
			.pipe(
				takeUntil(this.destroy$),
				filter(context => !!context)
			)
			.subscribe(context => {
				this.actionType = context.actionType;
				this.editEntityType = context.editEntityType;
				this.currentEntityDataForEdit = context.currentEntityForEdit;
			});
	}

	trackSidebarSubmitAction(): void {
		this.sidebarService.sidebarActionBtnClicked$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				//TODO this is a temporary fix for multiple click on submit button
				// will be rewritten in future workspace refactoring
				if (this.isProcessing) {
					return;
				}
				this.isProcessing = true;

				if (!this.assetForm.valid) {
					this.assetForm.markAllAsTouched();
					this.notify.warning(this.translateService.instant('required_fields_warning'));
					this.isProcessing = false;
					return;
				}

				if (this.actionType === 'EDIT') {
					this.attachToExisting();
				} else if (this.actionType === 'NEW') {
					this.attachForNew();
				} else if (this.assetForm.valid) {
					this.store$.dispatch(
						startCreateCmmsAsset({
							assets: [this.extractMutationVariablesFromForm()],
							entityToAttach: this.getEntityForAttach(),
							skipTableReload: !!this.externalData,
						})
					);
					this.sidebarService.clearNavigationHistory();
				} else {
					this.assetForm.markAllAsTouched();
					this.notify.warning(this.translateService.instant('required_fields_warning'));
				}
			});
	}

	trackSidebarCloseAction(): void {
		this.sidebarService.sidebarIsClosing$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.formNotChanged()) {
				this.sidebarService.closeAndClearPortal();
			} else {
				this.sidebarService.alertAboutDataLoss(this.assetForm.value);
			}
		});
	}

	attachToExisting() {
		let successMessage;

		this.assetsService
			.createAsset([this.extractMutationVariablesFromForm()])
			.pipe(
				take(1),
				mergeMap(data => {
					const assets = this.currentEntityDataForEdit.assets
						? this.currentEntityDataForEdit.assets.map(asset => {
								return { id: asset.pk, name: asset.name };
							})
						: [];

					data.forEach(asset => {
						assets.push({ id: asset.pk, name: asset.name });
					});

					const payload = {
						pk: this.currentEntityDataForEdit.pk,
						assets,
						name: this.currentEntityDataForEdit.name,
						description: this.currentEntityDataForEdit.description,
						id: null,
						notes: null,
					};
					let observable;

					if (this.editEntityType === 'INCIDENTS') {
						successMessage = `${this.translateService.instant('incident')} ${this.translateService.instant('has_been_updated_successfully')}`;
						observable = this.incidentService.editIncident(payload);
					} else if (this.editEntityType === 'WO') {
						successMessage = `${this.translateService.instant('work_order')} ${this.translateService.instant('has_been_updated_successfully')}`;
						observable = this.workOrdersService.updateWorkOrder(payload);
					} else if (this.editEntityType === 'REQUEST') {
						successMessage = `${this.translateService.instant('request')} ${this.translateService.instant('has_been_updated_successfully')}`;
						observable = this.requestService.editRequest(payload);
					} else if (this.editEntityType === 'FILE') {
						payload.id = this.currentEntityDataForEdit.id;
						payload.notes = this.currentEntityDataForEdit.notes;

						successMessage = `${this.translateService.instant('file')} ${this.translateService.instant('has_been_updated_successfully')}`;
						observable = this.filesService.editFile(payload).pipe(
							tap(file => {
								this.store$.dispatch(editFileSuccess({ file }));
							})
						);
					}

					return observable;
				})
			)
			.subscribe(() => {
				this.notify.success(successMessage);
				this.setActionTypeToEmpty();
				this.sidebarNavigation.navigateToPreviousPage();
			});
	}

	unlinkItem(
		item: IRequest | IIncident | ICmmsAsset,
		itemType: 'requests' | 'incidents' | 'workOrders'
	): void {
		this.assetForm
			.get(itemType)
			.patchValue(
				filterEntityByKey<typeof item>(this.assetForm.value[itemType], 'pk', item.pk)
			);
		this.cdr.markForCheck();
	}

	navigateTo(page: CmmsPages) {
		this.setNavigationConfigForHistory();

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					actionType: 'NEW',
					editEntityType: 'ASSET',
					currentEntityForEdit: this.assetForm.value,
				},
			})
		);

		this.sidebarNavigation.navigateTo(page);
	}

	navigateToExisting(pageData: { page: CmmsPages; tableType: TableType }) {
		this.setNavigationConfigForHistory();

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					tableTypeForExistingEntity: pageData.tableType,
					currentEntityForEdit: this.assetForm.value,
					editEntityType: 'ASSET',
				},
			})
		);

		this.sidebarNavigation.navigateTo(
			CmmsPages.existingEntities,
			sidebarHeaders[pageData.tableType]
		);
	}

	setNavigationConfigForHistory() {
		this.sidebarNavigation.setInitialComponent({
			initialComponent: RightSidebarAliasesEnum.newAsset,
			config: ACTIVE_PAGE_CONFIG[CmmsPages.newAsset].sidebarConfig,
			formValue: AuUtilsFunctions.deepCopyObject(this.assetForm.value),
			initialPage: CmmsPages.newAsset,
		});
	}

	attachForNew() {
		this.assetsService
			.createAsset([this.extractMutationVariablesFromForm()])
			.pipe(take(1))
			.subscribe(asets => {
				this.store$.dispatch(
					SetAttachFormValue({
						formValue: { assets: [asets[0]] },
					})
				);

				this.setActionTypeToEmpty();
				this.sidebarNavigation.navigateToPreviousPage();
			});
	}

	viewSelected(item: { data: any; entityType: CmmsPages }) {
		this.setNavigationConfigForHistory();
		this.sidebarService.viewCurrentEntity(NewAssetComponent, CmmsPages.newAsset, item);
		this.store$.dispatch(SetAttachFormValue({ formValue: this.assetForm.value }));
	}

	formNotChanged(): boolean {
		return !AuUtilsFunctions.hasBeenFilled(this.initialFormValue, this.assetForm.value);
	}

	addChildControl(name: string, control: FormControl) {
		this.assetForm.addControl(name, control);
		this.initialFormValue = this.assetForm.value;
	}

	fileListChanged($event: Partial<IDroppedFile>[]) {
		this.filesService.addOnlyNewFilesToForm($event, this.assetForm);
	}

	filesDeleted(event: { ids: string[]; onSuccess: () => void }) {
		this.filesService.deleteFileFromFormValue(event.ids, this.assetForm);
		this.fileSelectionsService.removeSelections(event.ids);
	}

	/**
	 * Use for get additional data for create asset
	 */
	getEntityForAttach(): ICmmsAssetEntityToAttach {
		const formValue = this.assetForm.value;
		return {
			workOrders: formValue.workOrders,
			incident: formValue.incidents,
			requests: formValue.requests,
			files: formValue.files,
		};
	}

	classificationListChanged($event: Pick<IClassification, 'pk'>[]) {
		this.assetForm.get('classifications').patchValue($event);
	}

	extractMutationVariablesFromForm(): Partial<ICmmsAssetCreateVariables> {
		const formValue = this.assetForm.value;

		const payload: Partial<ICmmsAssetCreateVariables> = {
			name: formValue.name,
			coordinates: formValue.coordinates,
			location: {
				type: formValue.location[0].locationType.toLowerCase(),
				id: formValue.location[0].locationId,
			},
			ifcId: {},
			description: formValue.description,
			classifications: getPrivateKeys(formValue.classifications),
			conditionGrade: formValue.conditionGrade,
			conditionGradeNote: formValue.conditionGradeNote,
			productName: formValue.productName,
			productYear: this.getProductYear(),
			serialNumber: formValue.serialNumber,
			installationDate: this.datePipe.transform(formValue.installationDate, 'yyyy-MM-dd'),
			installationCost: formValue.installationCost,
			warranty: this.datePipe.transform(formValue.warranty, 'yyyy-MM-dd'),
			deviationType: formValue.deviationType,
			riskLevel: formValue.riskLevel,
			consequenceRating: formValue.consequenceRating,
			costAllocation: formValue.costAllocation,
			timeframe: formValue.timeframe,
			costOfRepair: formValue.costOfRepair,
			labels: formValue.labels,
			matterportUrl: formValue.matterportUrl,
		};

		if (!payload.costOfRepair) {
			payload.costOfRepair = null;
		}

		return payload;
	}

	getProductYear() {
		const year = this.assetForm.value.productYear;
		return year ? `${year}-01-01` : null;
	}

	setActionTypeToEmpty() {
		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					actionType: null,
				},
			})
		);
	}

	conditionGradeChangedHandler(status: number) {
		this.conditionGradeExpanded.set(status !== null);

		const timeframeValue = timeframeMap[status] || null;

		this.assetForm.get('timeframe').setValue(timeframeValue);
		this.timeframeSignal.set(timeframeValue);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		if (this.externalData) {
			this.store$.dispatch(clearAssetExternalData());
		}
	}

	onAnalysisCompleted(event: IAiAssetAnalysisResponse): void {
		this.aiAnalysisResponse.set(event);
		this.applyAiAnalysisToForm(event);
	}

	onLoadingStateChanged(isLoading: boolean): void {
		this.isAiAnalysisLoading.set(isLoading);
	}

	private applyAiAnalysisToForm(analysisResponse: IAiAssetAnalysisResponse): void {
		const formUpdate = {
			name: analysisResponse.title,
			description: analysisResponse.description,
			productName: analysisResponse.product_name,
			serialNumber: analysisResponse.serial_number,
			productYear: analysisResponse.product_year,
			installationDate: analysisResponse.installation_date,
			installationCost: analysisResponse.installation_cost,
			warrantyExpiredDate: analysisResponse.warranty_expired_date,
			conditionGrade: analysisResponse.condition_grade,
			conditionGradeNote: analysisResponse.condition_grade_description,
			classifications: analysisResponse.building_component_classification,
		};

		this.assetForm.patchValue(formUpdate);

		if (analysisResponse.condition_grade) {
			this.conditionGradeExpanded.set(true);
			this.timeframeSignal.set(timeframeMap[analysisResponse.condition_grade] || '');
		}
	}

	onFilesAdded(newFiles: IAttachedFile[]): void {
		const currentFiles = this.assetForm.value.files || [];
		this.assetForm.patchValue({ files: [...currentFiles, ...newFiles] });
	}
}
